/**
 * SUNTRONIX ONE — Lifecycle Wheel.
 *
 * Signature visual: 8 lifecycle stages orbit a central Golden Asset
 * Record glyph, gently rotating.  The active stage receives a soft
 * glow synchronised with the story panel.  Honours prefers-reduced-
 * motion by freezing rotation.
 */
import { useEffect, useState } from "react";

const STAGES = [
  { label: "Procure",  colour: "#2962FF", icon: "M4 7h16M4 12h16M4 17h10" },
  { label: "Deploy",   colour: "#00C8FF", icon: "M12 3v18M3 12h18" },
  { label: "Manage",   colour: "#18D5A4", icon: "M4 6h16v12H4zM4 10h16" },
  { label: "Support",  colour: "#FFB84D", icon: "M12 2a10 10 0 100 20 10 10 0 000-20zM8 12h8" },
  { label: "Recover",  colour: "#7657FF", icon: "M4 12a8 8 0 0116 0M20 12l-3-3M20 12l-3 3" },
  { label: "Reuse",    colour: "#18D5A4", icon: "M6 3v4H2M18 21v-4h4M4 9a8 8 0 0114-3M20 15a8 8 0 01-14 3" },
  { label: "Redeploy", colour: "#2962FF", icon: "M21 3v6h-6M3 21v-6h6M3 9a9 9 0 0114-3M21 15a9 9 0 01-14 3" },
  { label: "Recycle",  colour: "#0F9F7A", icon: "M4 12l4-6h8l4 6M4 12l4 6h8l4-6M4 12h16" },
];

export default function LifecycleWheel({ activeStage = 0, onStageClick }) {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    setReduce(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);

  const size = 320;
  const radius = 118;
  const centre = size / 2;

  return (
    <div className="relative mx-auto" style={{ width: size, height: size }}
            data-testid="lifecycle-wheel">
      {/* Rotating outer ring — 60s per rotation */}
      <div className="absolute inset-0"
              style={{
                animation: reduce ? "none" : "wheel-rotate 60s linear infinite",
                transformOrigin: "50% 50%",
              }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
              className="absolute inset-0">
          <circle cx={centre} cy={centre} r={radius}
                     fill="none" stroke="rgba(198,210,234,0.14)" strokeWidth="1" />
          <circle cx={centre} cy={centre} r={radius + 22}
                     fill="none" stroke="rgba(198,210,234,0.08)"
                     strokeDasharray="2 6" strokeWidth="1" />
        </svg>
        {STAGES.map((s, i) => {
          const angle = (i / STAGES.length) * 2 * Math.PI - Math.PI / 2;
          const x = centre + radius * Math.cos(angle);
          const y = centre + radius * Math.sin(angle);
          const isActive = i === activeStage % STAGES.length;
          return (
            <div key={s.label}
                    style={{
                      position: "absolute",
                      left: x - 22, top: y - 22,
                      width: 44, height: 44,
                      // Counter-rotate labels so text stays upright
                      animation: reduce ? "none" : "wheel-rotate-reverse 60s linear infinite",
                      transformOrigin: "50% 50%",
                    }}>
              <button type="button"
                        onClick={() => onStageClick && onStageClick(i)}
                        aria-label={`Show ${s.label} panel`}
                        data-testid={`lifecycle-wheel-stage-${s.label.toLowerCase()}`}
                        className="w-full h-full grid place-items-center rounded-full transition-all duration-500 cursor-pointer hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/40"
                        style={{
                          background: isActive ? `${s.colour}26` : "rgba(255,255,255,0.03)",
                          border: `1px solid ${isActive ? s.colour + "80" : "rgba(255,255,255,0.10)"}`,
                          boxShadow: isActive ? `0 0 24px ${s.colour}80` : "none",
                        }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke={isActive ? s.colour : "rgba(198,210,234,0.6)"}
                      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d={s.icon} />
                </svg>
              </button>
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1.5 text-[9px] uppercase tracking-[0.14em] whitespace-nowrap"
                      style={{ color: isActive ? "#FFFFFF" : "rgba(198,210,234,0.65)" }}>
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Central Golden Asset Record glyph — stationary */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="relative">
          <div aria-hidden className="absolute inset-0 rounded-full blur-2xl bg-[#00C8FF] opacity-30" />
          <div className="relative h-24 w-24 rounded-full grid place-items-center bg-gradient-to-br from-white/95 to-[#EFF5FF] shadow-[0_10px_40px_-8px_rgba(0,200,255,0.55),inset_0_1px_0_rgba(255,255,255,0.9)]">
            <div className="text-center">
              <div className="text-[8px] tracking-[0.22em] uppercase text-[#2962FF] font-semibold">Golden</div>
              <div className="text-[16px] font-heading font-bold text-[#0D1B3D] tracking-tight leading-none">ONE</div>
              <div className="text-[8px] tracking-[0.22em] uppercase text-[#2962FF] font-semibold">Record</div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes wheel-rotate         { from { transform: rotate(0);      } to { transform: rotate(360deg);   } }
        @keyframes wheel-rotate-reverse { from { transform: rotate(0);      } to { transform: rotate(-360deg); } }
      `}</style>
    </div>
  );
}
